<script setup lang="ts">
import EosHostingClientSelect from '@/components/eos/common/EosHostingClientSelect.vue'
import RcSiteSelect, { type ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useSubmeters } from '@/composables/eos/submeters'
import { getHostingClientsReport, getInfrastructureReport, getSiteReport } from '@/services/eos/reports'
import { getSubmeterReport } from '@/services/eos/submeters'
import { SiteType } from '@/types/core/Site'
import {
  HostingClientReportType,
  InfrastructureReportType,
  SiteReportType,
  type IHostingClientsReportFilters,
  type IInfrastructureReportFilters,
  type ISiteReportFilters,
  type ISubmeterFilters,
} from '@/types/eos/filters'
import type { IHostingClient } from '@/types/eos/SiteMap'
import { InfrastructureType } from '@/types/eos/SiteMap'
import type { ISubmeter } from '@/types/eos/Submeters'
import { AllTimezones } from '@/types/shared/timezones'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { ElMessage } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { DateTime } from 'luxon'
import { computed, reactive, ref } from 'vue'

const oneMonthAgo = DateTime.now().minus({ month: 1 })

// Site Report refs
const selectedSiteReportsSite = ref<ISiteIdentifier>()
const selectedSiteReportsType = ref<SiteReportType>()
const isLoadingSiteReportsExport = ref<boolean>(false)
const selectedSiteReportsDateRange = ref<[Date, Date]>()
const siteReportsFilters = reactive<ISiteReportFilters>({
  start_datetime: oneMonthAgo.toISO() || '',
  end_datetime: DateTime.now().toISO() || '',
  type: SiteReportType.TABLE,
})

// Hosting Client Report refs
const selectedHostingClientReportMonth = ref<string>(oneMonthAgo.toFormat('yyyy-MM'))
const selectedHostingClientReportType = ref<HostingClientReportType>()
const selectedHostingClient = ref<IHostingClient>()
const selectedTimezone = ref<string>()
const isLoadingHostingClientReportsExport = ref<boolean>(false)
const hostingClientReportsFilters = reactive<IHostingClientsReportFilters>({
  month: oneMonthAgo.month,
  year: oneMonthAgo.year,
  type: HostingClientReportType.UPTIME,
})

// Infrastructure Report refs
const selectedInfrastructureReportMonth = ref<string>(oneMonthAgo.toFormat('yyyy-MM'))
const selectedInfrastructureType = ref<InfrastructureType>()
const selectedInfrastructureReportType = ref<InfrastructureReportType>()
const isLoadingInfrastructureReportsExport = ref<boolean>(false)
const infrastructureReportsFilters = reactive<IInfrastructureReportFilters>({
  month: oneMonthAgo.month,
  year: oneMonthAgo.year,
  type: InfrastructureReportType.UPTIME,
  infrastructure_type: InfrastructureType.SITE,
})

// Submeter Report refs
const selectedSubmeterReportMonth = ref<string>(oneMonthAgo.toFormat('yyyy-MM'))
const selectedSubmeter = ref<ISubmeter>()
const submeterSearchQuery = ref<string>('')
const isLoadingSubmeterReportsExport = ref<boolean>(false)
const { getSubmetersQuery } = useSubmeters()

const submeterFilters: ISubmeterFilters = {
  page: 1,
  per_page: 1000,
}

const { data: submetersData, isFetching: isFetchingSubmeters } = getSubmetersQuery(submeterFilters)

const submeterOptions = computed<OptionType[]>(() => {
  if (!submetersData.value) return []
  let filteredData = submetersData.value.data

  // Filter client-side if search query exists
  if (submeterSearchQuery.value) {
    const query = submeterSearchQuery.value.toLowerCase()
    filteredData = filteredData.filter(
      (submeter: ISubmeter) =>
        submeter.device_name.toLowerCase().includes(query) ||
        submeter.hosting_client?.name?.toLowerCase().includes(query)
    )
  }

  return filteredData.map((submeter: ISubmeter) => ({
    label: `${submeter.device_name} - ${submeter.hosting_client?.name || 'N/A'}`,
    value: submeter,
  }))
})

const handleSubmeterReportsMonthChanged = (newDate: string) => {
  if (newDate) {
    selectedSubmeterReportMonth.value = newDate
  }
}

const filterSubmeters = (query: string) => {
  submeterSearchQuery.value = query
}

const handleSubmeterReportsExport = async () => {
  if (!selectedSubmeter.value || !selectedSubmeterReportMonth.value) {
    return
  }

  try {
    isLoadingSubmeterReportsExport.value = true
    const date = DateTime.fromFormat(selectedSubmeterReportMonth.value, 'yyyy-MM')
    await getSubmeterReport({
      month: date.month,
      year: date.year,
      device_name: selectedSubmeter.value.device_name,
    })
  } catch (err: any) {
    ElMessage.error(err.message)
  } finally {
    isLoadingSubmeterReportsExport.value = false
  }
}

/**
 * Disables future dates in the date picker
 */
const disableFutureDates = (date: Date) => {
  const selectedDate = DateTime.fromJSDate(date)
  const currentDate = DateTime.now()
  return selectedDate > currentDate
}

const handleSiteReportsDateRangeChanged = (newDateRange: [Date, Date] | null) => {
  if (newDateRange) {
    selectedSiteReportsDateRange.value = newDateRange
    siteReportsFilters.start_datetime = DateTime.fromJSDate(newDateRange[0]).toISO() || ''
    siteReportsFilters.end_datetime = DateTime.fromJSDate(newDateRange[1]).toISO() || ''
  }
}

const handleSiteReportsTypeChange = (type: SiteReportType) => {
  selectedSiteReportsType.value = type
  siteReportsFilters.type = type
}

const handleSiteReportsExport = async () => {
  if (!selectedSiteReportsSite.value) {
    return
  }

  // If the selected site has a timezone, convert the selectedSiteReportsDateRange dates to the site's timezone
  // while keeping the same hours
  if (selectedSiteReportsSite.value.timezone) {
    siteReportsFilters.start_datetime =
      DateTime.fromJSDate(selectedSiteReportsDateRange.value![0])
        .setZone(selectedSiteReportsSite.value.timezone, { keepLocalTime: true })
        .toISO() || ''
    siteReportsFilters.end_datetime =
      DateTime.fromJSDate(selectedSiteReportsDateRange.value![1])
        .setZone(selectedSiteReportsSite.value.timezone, { keepLocalTime: true })
        .toISO() || ''
  }

  try {
    isLoadingSiteReportsExport.value = true
    await getSiteReport(selectedSiteReportsSite.value.site_id!, siteReportsFilters)
  } catch (err: any) {
    ElMessage.error(err.message)
  } finally {
    isLoadingSiteReportsExport.value = false
  }
}

const handleHostingClientReportsMonthChanged = (newDate: string) => {
  if (newDate) {
    selectedHostingClientReportMonth.value = newDate
    const date = DateTime.fromFormat(newDate, 'yyyy-MM')
    hostingClientReportsFilters.month = date.month
    hostingClientReportsFilters.year = date.year
  }
}

const handleHostingClientReportsTypeChange = (type: HostingClientReportType) => {
  selectedHostingClientReportType.value = type
  hostingClientReportsFilters.type = type

  // Clear additional fields when switching away from BILLING
  if (type !== HostingClientReportType.BILLING) {
    selectedHostingClient.value = undefined
    selectedTimezone.value = undefined
    hostingClientReportsFilters.hosting_client_id = undefined
    hostingClientReportsFilters.timezone = undefined
  }
}

const handleHostingClientChange = (client: IHostingClient | undefined) => {
  selectedHostingClient.value = client
  hostingClientReportsFilters.hosting_client_id = client?.id
  hostingClientReportsFilters.hosting_client_name = client?.name
}

const handleTimezoneChange = (timezone: string) => {
  selectedTimezone.value = timezone
  hostingClientReportsFilters.timezone = timezone
}

const handleHostingClientReportsExport = async () => {
  try {
    isLoadingHostingClientReportsExport.value = true
    await getHostingClientsReport(hostingClientReportsFilters)
  } catch (err: any) {
    ElMessage.error(err.message)
  } finally {
    isLoadingHostingClientReportsExport.value = false
  }
}

const handleInfrastructureTypeChange = (type: InfrastructureType) => {
  selectedInfrastructureType.value = type
  infrastructureReportsFilters.infrastructure_type = type
}

const handleInfrastructureReportsMonthChanged = (newDate: string) => {
  if (newDate) {
    selectedInfrastructureReportMonth.value = newDate
    const date = DateTime.fromFormat(newDate, 'yyyy-MM')
    infrastructureReportsFilters.month = date.month
    infrastructureReportsFilters.year = date.year
  }
}

const handleInfrastructureReportsTypeChange = (type: InfrastructureReportType) => {
  selectedInfrastructureReportType.value = type
  infrastructureReportsFilters.type = type
}

const handleInfrastructureReportsExport = async () => {
  try {
    isLoadingInfrastructureReportsExport.value = true
    await getInfrastructureReport(infrastructureReportsFilters)
  } catch (err: any) {
    ElMessage.error(err.message)
  } finally {
    isLoadingInfrastructureReportsExport.value = false
  }
}
</script>

<template>
  <ViewLayout>
    <div class="flex gap-4">
      <el-card class="w-[320px]">
        <div class="flex flex-col gap-6">
          <header>
            <h3 class="text-lg font-semibold">Site Reports</h3>
          </header>
          <div class="flex flex-col gap-4">
            <div>
              <el-date-picker
                v-model="selectedSiteReportsDateRange"
                type="daterange"
                range-separator="To"
                start-placeholder="Start"
                end-placeholder="End"
                format="YYYY-MM-DD"
                style="width: 100%"
                :disabled-date="disableFutureDates"
                @change="handleSiteReportsDateRangeChanged"
              />
            </div>
            <div>
              <el-select
                v-model="selectedSiteReportsType"
                placeholder="Select a report"
                @change="handleSiteReportsTypeChange"
              >
                <el-option :key="SiteReportType.TABLE" :value="SiteReportType.TABLE" label="Table Report" />
                <el-option :key="SiteReportType.DOWNTIME" :value="SiteReportType.DOWNTIME" label="Downtime Report" />
                <el-option :key="SiteReportType.BASELINE" :value="SiteReportType.BASELINE" label="Baseline Report" />
              </el-select>
            </div>
            <div>
              <RcSiteSelect v-model="selectedSiteReportsSite" :type="SiteType.DataCenter" placeholder="Select Site" />
            </div>
          </div>
          <div class="flex justify-end">
            <el-button
              type="primary"
              :loading="isLoadingSiteReportsExport"
              :disabled="!selectedSiteReportsDateRange || !selectedSiteReportsSite || isLoadingSiteReportsExport"
              @click="handleSiteReportsExport"
            >
              Export
            </el-button>
          </div>
        </div>
      </el-card>

      <el-card class="w-[320px]">
        <div class="flex flex-col gap-6">
          <header>
            <h3 class="text-lg font-semibold">Hosting Clients Reports</h3>
          </header>
          <div class="flex flex-col gap-4">
            <div>
              <el-date-picker
                v-model="selectedHostingClientReportMonth"
                type="month"
                placeholder="Select month"
                format="MMMM YYYY"
                value-format="YYYY-MM"
                date-format="MMMM yyyy"
                :disabled-date="disableFutureDates"
                @change="handleHostingClientReportsMonthChanged"
              />
            </div>
            <div>
              <el-select
                v-model="selectedHostingClientReportType"
                placeholder="Select a report"
                clearable
                @change="handleHostingClientReportsTypeChange"
              >
                <el-option
                  :key="HostingClientReportType.UPTIME"
                  :value="HostingClientReportType.UPTIME"
                  label="Uptime Report"
                />
                <el-option
                  :key="HostingClientReportType.BILLING"
                  :value="HostingClientReportType.BILLING"
                  label="Billing Report"
                />
              </el-select>
            </div>
            <div v-if="selectedHostingClientReportType === HostingClientReportType.BILLING" class="flex flex-col gap-4">
              <div>
                <EosHostingClientSelect
                  v-model="selectedHostingClient"
                  placeholder="Select Hosting Client"
                  @change="handleHostingClientChange"
                />
              </div>
              <div>
                <el-select
                  v-model="selectedTimezone"
                  placeholder="Select timezone"
                  filterable
                  @change="handleTimezoneChange"
                >
                  <el-option v-for="timezone in AllTimezones" :key="timezone" :label="timezone" :value="timezone" />
                </el-select>
              </div>
            </div>
            <div v-else style="height: 40px"></div>
          </div>
          <div class="flex justify-end">
            <el-button
              type="primary"
              :loading="isLoadingHostingClientReportsExport"
              :disabled="
                !selectedHostingClientReportMonth ||
                !selectedHostingClientReportType ||
                (selectedHostingClientReportType === HostingClientReportType.BILLING &&
                  (!selectedHostingClient || !selectedTimezone)) ||
                isLoadingHostingClientReportsExport
              "
              @click="handleHostingClientReportsExport"
            >
              Export
            </el-button>
          </div>
        </div>
      </el-card>

      <el-card class="w-[320px]">
        <div class="flex flex-col gap-6">
          <header>
            <h3 class="text-lg font-semibold">Infrastructure Reports</h3>
          </header>
          <div class="flex flex-col gap-4">
            <div>
              <el-date-picker
                v-model="selectedInfrastructureReportMonth"
                type="month"
                placeholder="Select month"
                format="MMMM YYYY"
                value-format="YYYY-MM"
                date-format="MMMM yyyy"
                :disabled-date="disableFutureDates"
                @change="handleInfrastructureReportsMonthChanged"
              />
            </div>
            <div>
              <el-select
                v-model="selectedInfrastructureReportType"
                placeholder="Select a report"
                @change="handleInfrastructureReportsTypeChange"
              >
                <el-option :value="InfrastructureReportType.UPTIME" label="Uptime Report" />
              </el-select>
            </div>
            <div>
              <el-select
                v-model="selectedInfrastructureType"
                placeholder="Select infrastructure type"
                @change="handleInfrastructureTypeChange"
              >
                <el-option :value="InfrastructureType.SITE" :label="InfrastructureType.SITE" />
                <el-option :value="InfrastructureType.STRUCTURE" :label="InfrastructureType.STRUCTURE" />
                <el-option :value="InfrastructureType.RACK" :label="InfrastructureType.RACK" />
              </el-select>
            </div>
          </div>
          <div class="flex justify-end">
            <el-button
              type="primary"
              :loading="isLoadingInfrastructureReportsExport"
              :disabled="
                !selectedInfrastructureReportMonth ||
                !selectedInfrastructureType ||
                isLoadingInfrastructureReportsExport
              "
              @click="handleInfrastructureReportsExport"
            >
              Export
            </el-button>
          </div>
        </div>
      </el-card>

      <el-card class="w-[320px]">
        <div class="flex flex-col gap-6">
          <header>
            <h3 class="text-lg font-semibold">Submeter Reports</h3>
          </header>
          <div class="flex flex-col gap-4">
            <div>
              <el-date-picker
                v-model="selectedSubmeterReportMonth"
                type="month"
                placeholder="Select month"
                format="MMMM YYYY"
                value-format="YYYY-MM"
                date-format="MMMM yyyy"
                :disabled-date="disableFutureDates"
                @change="handleSubmeterReportsMonthChanged"
              />
            </div>
            <div>
              <el-select-v2
                v-model="selectedSubmeter"
                :options="submeterOptions"
                :loading="isFetchingSubmeters"
                remote
                :remote-method="filterSubmeters"
                filterable
                clearable
                value-key="id"
                placeholder="Select submeter"
                loading-text="Loading..."
                :item-height="72"
                style="width: 100%"
              >
                <template #default="{ item }">
                  <div class="h-full w-full flex flex-col justify-center items-center gap-2">
                    <header class="w-full text-sm inline-flex justify-between items-center">
                      <span class="font-bold">{{ item.value.device_name }}</span>
                    </header>
                    <div class="w-full">
                      <p class="text-xs text-ellipsis overflow-hidden">
                        <span class="text-tertiary">Owner:</span>
                        <span class="ml-1">{{ item.value.hosting_client?.name || 'N/A' }}</span>
                      </p>
                    </div>
                  </div>
                </template>
              </el-select-v2>
            </div>
          </div>
          <div class="flex justify-end">
            <el-button
              type="primary"
              :loading="isLoadingSubmeterReportsExport"
              :disabled="!selectedSubmeterReportMonth || !selectedSubmeter || isLoadingSubmeterReportsExport"
              @click="handleSubmeterReportsExport"
            >
              Export
            </el-button>
          </div>
        </div>
      </el-card>
    </div>
  </ViewLayout>
</template>
